import React from "react";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import { Link } from "react-router-dom";
import about from "../assets/images/About.png";

const missionData = [
  {
    title: "Experienced faculty with industry expertise",
    image_url: "https://img.icons8.com/color/48/000000/open-book.png",
  },
  {
    title: "Personalized learning paths for every student",
    image_url: "https://img.icons8.com/color/48/000000/maintenance.png",
  },
  {
    title: "Hands-on projects and practical training",
    image_url: "https://img.icons8.com/color/48/000000/maintenance.png",
  },
  {
    title: "Proven track record of student success",
    image_url: "https://img.icons8.com/color/48/000000/trophy.png",
  },
];

const AboutSection = ({ variant = "home", scrollToContact }) => {
  return (
    <div className="py-16 px-6 lg:px-20 bg-white">
      <div className="text-center mb-12">
        <p
          style={{ letterSpacing: "0.15rem", lineHeight: "1.25rem" }}
          className="uppercase text-sm md:text-base text-gray-700 font-bold"
        >
          About Us
        </p>
        <h2 className="font-bold text-2xl mt-9 md:text-5xl text-gray-700">
          Shaping Futures with Knowledge & Skills
        </h2>
      </div>

      {variant === "home" && (
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-lg text-gray-600 max-w-4xl mx-auto text-start">
            We are a learning institute committed to empowering students with
            real-world skills and hands-on knowledge. Our mission is to guide
            learners to achieve excellence through structured training and
            personalized mentorship.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {missionData.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg shadow-sm"
              >
                <img src={item.image_url} alt="" className="w-10 h-10" />
                <p className="text-gray-700 font-medium">{item.title}</p>
              </div>
            ))}
          </div>

          <Link
            to="/about"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-10 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center shadow-lg hover:scale-105 transition"
          >
            Show More <FaArrowRight className="ml-2" />
          </Link>
        </div>
      )}

      {variant === "page" && (
        <div className="max-w-7xl mx-auto flex flex-col gap-16">
          <div className="flex flex-col lg:flex-row items-center gap-10 text-start">
            <div className="w-full lg:w-1/2">
              <img
                src={about}
                alt="Our Institute"
                className="rounded-lg shadow-lg w-full object-cover"
              />
            </div>

            <div className="w-full lg:w-1/2 text-gray-700">
              <h3 className="text-3xl font-bold mb-4">
                A Smarter, Leaner, More Focused Way to Learn
              </h3>
              <p className="text-base mb-4">
                At our institute, we are not just teachers – we are mentors and
                partners in your success journey. No generic lessons, no
                one-size-fits-all. Just real educators with practical expertise
                guiding you at every step.
              </p>
              <p className="text-base mb-4">
                While traditional institutes focus only on syllabus, we go
                beyond. Our programs include real-world projects, soft skills,
                and career guidance to make you industry-ready.
              </p>

              <p className="font-semibold mb-4">Why Students Choose Us:</p>
              <ul className="space-y-4">
                {[
                  {
                    title: "Expert Trainers",
                    desc: "Learn directly from professionals with years of experience.",
                  },
                  {
                    title: "Hands-on Learning",
                    desc: "Real projects and practical exposure for industry readiness.",
                  },
                  {
                    title: "Focused Attention",
                    desc: "Small batches for personalized mentorship and growth.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center rounded-full w-10 h-10 mr-3 text-white">
                      <FaCheck />
                    </span>
                    <span>
                      <strong>{item.title}:</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={scrollToContact}
                className="mt-8 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full font-semibold inline-flex items-center shadow-lg hover:scale-105 transition"
              >
                Get in Touch <FaArrowRight className="ml-2" />
              </button>
            </div>
          </div>

          {/* About, Mission, Vision, Values, Stats, Journey, CTA */}
          <div className="flex flex-col gap-20 text-center">
            {/* Intro */}
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-800">
                About Our Institute
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We are a modern learning institute dedicated to equipping
                students with real-world skills. Our approach blends expert
                guidance, hands-on projects, and strong mentorship to help
                learners achieve their goals.
              </p>
            </div>

            {/* Mission, Vision, Values Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Our Mission",
                  desc: "To empower learners with knowledge, skills, and confidence to succeed.",
                  icon: "🎯",
                },
                {
                  title: "Our Vision",
                  desc: "To be a globally recognized institute known for innovation and excellence.",
                  icon: "🌍",
                },
                {
                  title: "Our Values",
                  desc: "Integrity, Innovation, Collaboration, and Lifelong Learning.",
                  icon: "💡",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-8 bg-white rounded-xl shadow-lg hover:shadow-2xl transition transform hover:-translate-y-2"
                >
                  <div className="text-5xl mb-4">{item.icon}</div>
                  <h4 className="text-xl font-semibold mb-3 text-gray-800">
                    {item.title}
                  </h4>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Stats Section */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-16 rounded-xl shadow-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div>
                  <p className="text-5xl font-bold">5000+</p>
                  <p>Students</p>
                </div>
                <div>
                  <p className="text-5xl font-bold">50+</p>
                  <p>Courses</p>
                </div>
                <div>
                  <p className="text-5xl font-bold">90%</p>
                  <p>Placements</p>
                </div>
                <div>
                  <p className="text-5xl font-bold">10+</p>
                  <p>Years</p>
                </div>
              </div>
            </div>

{/* Enhanced Timeline / Journey */}
<div className="bg-gray-100 py-28 relative overflow-hidden">
  <h3 className="text-4xl md:text-5xl font-bold text-center mb-20 text-gray-800">
    Our Journey
  </h3>

  <div className="max-w-6xl mx-auto relative">
    {/* Vertical Gradient Line */}
    <div className="hidden md:block absolute left-1/2 top-0 w-1 bg-gradient-to-b from-purple-400 to-pink-400 h-full -translate-x-1/2"></div>

    <div className="flex flex-col space-y-16">
      {[
        {
          year: "2015",
          title: "Foundation Laid",
          text: "Founded with the vision to provide affordable quality education.",
          stats: "100+ Students Enrolled",
          icon: "🎓",
          color: "from-purple-500 to-pink-500",
        },
        {
          year: "2017",
          title: "First Milestone",
          text: "Trained our first 1000 students across multiple domains.",
          stats: "1000+ Students Trained",
          icon: "📚",
          color: "from-green-400 to-teal-500",
        },
        {
          year: "2020",
          title: "Recognition Achieved",
          text: "Recognized as a leading institute for practical learning.",
          stats: "Top Rated Institute",
          icon: "🏆",
          color: "from-yellow-400 to-orange-500",
        },
        {
          year: "2023",
          title: "Global Expansion",
          text: "Expanded globally with online and hybrid programs.",
          stats: "Students Worldwide",
          icon: "🌍",
          color: "from-blue-500 to-indigo-500",
        },
      ].map((item, i) => (
        <div
          key={i}
          className={`flex flex-col md:flex-row ${
            i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
          } items-center gap-8 relative group`}
        >
          {/* Icon Circle */}
          <div className="flex-shrink-0 relative">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl text-white bg-gradient-to-r ${item.color} shadow-lg transform transition group-hover:scale-110`}
            >
              {item.icon}
            </div>
          </div>

          {/* Card */}
          <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition transform group-hover:-translate-y-3 flex-1">
            <h4 className="text-2xl font-bold text-gray-800 mb-2">{item.year} - {item.title}</h4>
            <p className="text-gray-600 mb-4">{item.text}</p>
            <span className="inline-block mt-2 font-semibold text-purple-600">{item.stats}</span>
          </div>

          {/* Decorative circles */}
          <div className="absolute -z-10 w-32 h-32 rounded-full bg-gradient-to-r from-purple-200 to-pink-200 opacity-30 top-0 right-0 transform translate-x-1/2 -translate-y-1/2"></div>
        </div>
      ))}

      {/* CTA Section */}
      <div className="flex justify-center mt-12">
        <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-10 rounded-3xl shadow-2xl text-center max-w-xl hover:scale-105 transition transform">
          <h4 className="text-3xl font-bold mb-4">Join Our Journey</h4>
          <p className="mb-6">Be part of our success story and achieve your dreams with us.</p>
          <button className="bg-white text-purple-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition">
            Get in Touch
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

    
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutSection;
